import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { formatLastFirstName, patientNameFields } from "../../../utils/helper";

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 8,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    color: "#333333",
    flexDirection: "column"
  },
  header: {
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingBottom: 5
  },
  logo: {
    width: 80,
    height: 20,
    objectFit: "contain"
  },
  titleContainer: {
    textAlign: "right"
  },
  reportTitle: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: "#222222"
  },
  reportDate: {
    fontSize: 9,
    color: "#666"
  },
  table: {
    width: "100%",
    borderWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#eeeeee",
    paddingVertical: 4,
    alignItems: "center",
    minHeight: 15
  },
  tableHeader: {
    backgroundColor: "#f4f6f8",
    borderBottomWidth: 1,
    borderBottomColor: "#dddddd",
    paddingVertical: 6
  },
  cell: {
    paddingHorizontal: 2,
    fontSize: 7,
    textAlign: "left"
  },
  headerCell: {
    fontFamily: "Helvetica-Bold",
    color: "#444",
    fontSize: 7
  },
  watermark: {
    position: "absolute",
    top: "50%",
    left: "12.5%",
    width: "80%",
    opacity: 0.03,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    borderTopWidth: 0.5,
    borderTopColor: '#cccccc',
    paddingTop: 5,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 7,
    color: '#888888'
  },
  stackedCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 2
  }
});

const BillingReportPDF = ({
  data,
  date,
  type,
  logo,
  formatDate,
  formatUSA,
  formatSecondsToTime,
  convertUTCToLocal,
  // SCRUM-732: mirrors the Billing Dashboard's column-preference popup -
  // Patient Name/DOB/CPT codes aren't user-configurable there either, so
  // they're always rendered here regardless of this prop.
  visibleColumns = { provider: true, icd: true, consent: true, dos: true }
}) => {
  const isRPM = type === 'rpm';
  
  // Define columns based on type
  const colWidths = isRPM 
    ? { 
        name: '12%', 
        dob: '7%', 
        provider: '10%', 
        icd: '9%', 
        consent: '9%',
        serviceDate: '9%',
        code1: '7%',
        code2: '7%',
        code3: '7%',
        code4: '7%',
        code5: '7%',
        code6: '7%'
      }
    : { 
        name: '13%', 
        dob: '8%', 
        provider: '11%', 
        icd: '10%', 
        consent: '10%',
        code1: '8%',
        code2: '8%',
        code3: '8%',
        code4: '8%',
        code5: '8%'
      };

  const title = isRPM ? 'RPM Billing Report' : 'CCM Billing Report';

  const renderCodeStatus = (billings, code) => {
    // Find all billings for this code, EXCLUDING non_billable
    const matchingBillings = billings?.filter(b => b.code === code && b.status !== 'non_billable') || [];
    
    if (matchingBillings.length === 0) return <Text>-</Text>;

    // If multiple billings, render stacked Text
    return (
        <View style={styles.stackedCell}>
            {matchingBillings.map((bill, idx) => {
                 const units = bill.units > 1 ? ` x${bill.units}` : "";
                 return <Text key={idx}>{`${bill.status}${units}`}</Text>;
            })}
        </View>
    );
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Header */}
        <View style={styles.header} fixed>
          <Image src={logo} style={styles.logo} />
          <View style={styles.titleContainer}>
            <Text style={styles.reportTitle}>{title}</Text>
            <Text style={styles.reportDate}>{formatDate(date)}</Text>
          </View>
        </View>

        {/* Table Header */}
        <View style={[styles.tableRow, styles.tableHeader]} fixed>
          <Text style={[styles.cell, styles.headerCell, { width: colWidths.name }]}>Patient Name</Text>
          <Text style={[styles.cell, styles.headerCell, { width: colWidths.dob }]}>DOB</Text>
          {visibleColumns.provider && (
            <Text style={[styles.cell, styles.headerCell, { width: colWidths.provider }]}>Provider</Text>
          )}
          {visibleColumns.icd && (
            <Text style={[styles.cell, styles.headerCell, { width: colWidths.icd }]}>ICD-10</Text>
          )}
          {visibleColumns.consent && (
            <Text style={[styles.cell, styles.headerCell, { width: colWidths.consent }]}>Consent Date</Text>
          )}

          {isRPM && visibleColumns.dos && (
            <Text style={[styles.cell, styles.headerCell, { width: colWidths.serviceDate }]}>DOS</Text>
          )}

          {isRPM ? (
            <>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code1 }]}>99453</Text>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code2 }]}>99445</Text>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code3 }]}>99454</Text>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code4 }]}>99470</Text>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code5 }]}>99457</Text>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code6 }]}>99458</Text>
            </>
          ) : (
            <>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code1 }]}>G0506</Text>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code2 }]}>99490</Text>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code3 }]}>99439</Text>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code4 }]}>99487</Text>
                <Text style={[styles.cell, styles.headerCell, { width: colWidths.code5 }]}>99489</Text>
            </>
          )}
        </View>

        {/* Rows */}
        {data?.patients?.map((patient, index) => {
          const icds = isRPM 
            ? (patient.rpm_codes?.join(", ") || "--")
            : (patient.icd_codes?.join(", ") || "--");

          const consentDateRaw = isRPM ? (patient.RPM_setup_date || patient.created_at) : patient.created_at;
          const consentDateFormatted = consentDateRaw ? formatUSA(consentDateRaw) : "--";
          const dobFormatted = patient.dob ? formatUSA(patient.dob) : "--";
          const billings = isRPM ? patient.rpm_billings : patient.ccm_billings;

         // Logic for DOS Column in PDF
          let dosRender = <Text>--</Text>;
          if (isRPM) {
             const targetCodes = ["99445", "99454"];
             const relevantBillings = patient.rpm_billings?.filter(b => targetCodes.includes(b.code)) || [];
             if (relevantBillings.length > 0) {
                 const entries = [];
                 relevantBillings.forEach(bill => {
                     if (bill.dates_of_service) {
                         bill.dates_of_service.forEach(dateStr => {
                             const [y, m, d] = dateStr.split('-');
                             // Store as object
                             entries.push({ date: `${m}-${d}-${y}`, code: bill.code });
                         });
                     }
                 });
                 if (entries.length > 0) {
                     dosRender = (
                         <View style={styles.stackedCell}>
                             {entries.map((entry, i) => (
                                <Text key={i}>
                                  {/* using Helvetica-Oblique to force italics on standard font */}
                                  {entry.date} <Text style={{ color: '#888888', fontFamily: 'Helvetica-Oblique'}}>({entry.code})</Text>
                                </Text>
                             ))}
                         </View>
                     );
                 }
             } else {
                 // Fallback
                 const serviceDate = convertUTCToLocal(patient.code_stats?.[0]?.[99454]?.created_at) || "--";
                 dosRender = <Text>{serviceDate}</Text>;
             }
          }

          return (
            <View key={index} style={[styles.tableRow, { backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9' }]} wrap={false}>
              <Text style={[styles.cell, { width: colWidths.name }]}>{formatLastFirstName(patientNameFields(patient))}</Text>
              <Text style={[styles.cell, { width: colWidths.dob }]}>{dobFormatted}</Text>
              {visibleColumns.provider && (
                <Text style={[styles.cell, { width: colWidths.provider }]}>{patient.provider || "--"}</Text>
              )}
              {visibleColumns.icd && (
                <Text style={[styles.cell, { width: colWidths.icd }]}>{icds}</Text>
              )}
              {visibleColumns.consent && (
                <Text style={[styles.cell, { width: colWidths.consent }]}>{consentDateFormatted}</Text>
              )}

              {isRPM && visibleColumns.dos && (
                <View style={[styles.cell, { width: colWidths.serviceDate }]}>
                    {dosRender}
                </View>
              )}

              {isRPM ? (
                <>
                    <View style={[styles.cell, { width: colWidths.code1 }]}>{renderCodeStatus(billings, "99453")}</View>
                    <View style={[styles.cell, { width: colWidths.code2 }]}>{renderCodeStatus(billings, "99445")}</View>
                    <View style={[styles.cell, { width: colWidths.code3 }]}>{renderCodeStatus(billings, "99454")}</View>
                    <View style={[styles.cell, { width: colWidths.code4 }]}>{renderCodeStatus(billings, "99470")}</View>
                    <View style={[styles.cell, { width: colWidths.code5 }]}>{renderCodeStatus(billings, "99457")}</View>
                    <View style={[styles.cell, { width: colWidths.code6 }]}>{renderCodeStatus(billings, "99458")}</View>
                </>
              ) : (
                <>
                    <View style={[styles.cell, { width: colWidths.code1 }]}>{renderCodeStatus(billings, "G0506")}</View>
                    <View style={[styles.cell, { width: colWidths.code2 }]}>{renderCodeStatus(billings, "99490")}</View>
                    <View style={[styles.cell, { width: colWidths.code3 }]}>{renderCodeStatus(billings, "99439")}</View>
                    <View style={[styles.cell, { width: colWidths.code4 }]}>{renderCodeStatus(billings, "99487")}</View>
                    <View style={[styles.cell, { width: colWidths.code5 }]}>{renderCodeStatus(billings, "99489")}</View>
                </>
              )}
            </View>
          );
        })}

        <View style={styles.footer} fixed>
          <Text>{JSON.parse(sessionStorage.getItem("current_loc"))?.name || "Clinic"}</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} of ${totalPages}`} />
          <Text>{new Date().toLocaleString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
          })}</Text>
        </View>
        <Image src={logo} style={styles.watermark} fixed />
      </Page>
    </Document>
  );
};

export default BillingReportPDF;